export class PaymentCondition {
  id: string;
  days: number;
  discount_rate: number;

  constructor(json?: any) {
    if (json) {
      this.id = json.id;
      this.days = json.days;
      this.discount_rate = json.discount_rate;
    }
    if (!this.id) {
      // hack : generate random id for table management
      this.id = generateObjectId();
    }
  }

  public static createInstance(jsonData: any): PaymentCondition {
    return Object.assign(new PaymentCondition(), jsonData);
  }

  public static createInstances(jsonData: any[]): PaymentCondition[] {
    return jsonData.map(PaymentCondition.createInstance);
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}

export const initPaymentConditions: PaymentCondition[] = [
  new PaymentCondition({
    days: 30,
    discount_rate: 0,
  }),
];

export const PAYMENT_CONDITIONS_TAG = "{{CONDITIONS_PAIEMENT}}";
const DEFAULT_PAYMENT_DAYS = 30;

/** Conditions du client, à défaut celles de l'entreprise, à défaut 30 jours net. */
export function resolvePaymentConditions(
  customerConditions: PaymentCondition[] | null | undefined,
  companyConditions: PaymentCondition[] | null | undefined,
): PaymentCondition[] {
  if (customerConditions?.length) return customerConditions;
  if (companyConditions?.length) return companyConditions;
  return initPaymentConditions;
}

const isDiscounted = (c: PaymentCondition) => Number(c.discount_rate) > 0;
const daysOf = (c: PaymentCondition) => Number(c.days) || 0;

/** Délai de paiement en jours : le plus long délai net, à défaut le plus long délai, à défaut 30. */
export function paymentDays(conditions: PaymentCondition[]): number {
  const net = conditions.filter((c) => !isDiscounted(c));
  const source = net.length ? net : conditions;
  return source.length ? Math.max(...source.map(daysOf)) : DEFAULT_PAYMENT_DAYS;
}

/** Échéance = date de facture + délai de paiement (cf. paymentDays). */
export function paymentDeadlineFrom(invoiceDate: Date, conditions: PaymentCondition[]): Date {
  return deadlineAfter(invoiceDate, paymentDays(conditions));
}

export function deadlineAfter(invoiceDate: Date, days: number): Date {
  const deadline = new Date(invoiceDate);
  deadline.setDate(deadline.getDate() + days);
  return deadline;
}

const PAYMENT_DEADLINE_SHORTCUT_DAYS = [30, 45, 60];

/** Boutons d'échéance : 30, 45, 60 jours + délais des conditions, sans doublon, triés. */
export function paymentDeadlineShortcutDays(conditions: PaymentCondition[] | null | undefined): number[] {
  const days = [...PAYMENT_DEADLINE_SHORTCUT_DAYS, ...(conditions ?? []).map(daysOf)].filter((d) => d > 0);
  return [...new Set(days)].sort((a, b) => a - b);
}

function daysLabel(days: number): string {
  const n = Number(days) || 0;
  return n === 1 ? `${n} jour` : `${n} jours`;
}

export function paymentConditionLabel(condition: PaymentCondition): string {
  return isDiscounted(condition)
    ? `Escompte de ${Number(condition.discount_rate)} % si payé dans les ${daysLabel(condition.days)}`
    : `Paiement net dans les ${daysLabel(condition.days)}`;
}

export function paymentConditionsLines(conditions: PaymentCondition[]): string[] {
  const byDays = (a: PaymentCondition, b: PaymentCondition) => daysOf(a) - daysOf(b);
  const discounted = conditions.filter(isDiscounted).sort(byDays);
  const net = conditions.filter((c) => !isDiscounted(c)).sort(byDays);
  return [...discounted, ...net].map(paymentConditionLabel);
}

/** Incohérences de saisie à signaler sous l'éditeur (n'empêchent pas l'enregistrement). */
export function paymentConditionsWarnings(conditions: PaymentCondition[]): string[] {
  const warnings: string[] = [];
  if (conditions.some((c) => daysOf(c) <= 0)) {
    warnings.push("Un délai est à 0 jour : la facture serait payable le jour même.");
  }
  const days = conditions.map(daysOf);
  if (new Set(days).size !== days.length) {
    warnings.push("Deux conditions ont le même délai.");
  }
  const net = conditions.filter((c) => !isDiscounted(c));
  if (conditions.length && !net.length) {
    warnings.push("Aucun délai net : la date limite de paiement reprendra le délai de l'escompte.");
  }
  const longestNet = net.length ? Math.max(...net.map(daysOf)) : null;
  if (longestNet !== null && conditions.some((c) => isDiscounted(c) && daysOf(c) >= longestNet)) {
    warnings.push("Un escompte a un délai égal ou plus long que le délai net.");
  }
  return warnings;
}

export function hasPaymentConditionsTag(html: string | null | undefined): boolean {
  return !!html?.includes(PAYMENT_CONDITIONS_TAG);
}

/** Délai déjà écrit en dur dans un texte de modalités (« 30 jours »), sinon null. */
export function hardcodedPaymentDelay(html: string | null | undefined): string | null {
  const text = (html ?? "").replace(PAYMENT_CONDITIONS_TAG, "").replace(/<[^>]*>/g, " ");
  return text.match(/\d+\s*jours?\b/i)?.[0] ?? null;
}

export function paymentConditionsHtml(conditions: PaymentCondition[]): string {
  return paymentConditionsLines(conditions)
    .map((line) => `<p>${line}</p>`)
    .join("");
}

export function applyPaymentConditionsTag(html: string, conditions: PaymentCondition[]): string {
  if (!html?.includes(PAYMENT_CONDITIONS_TAG)) return html;
  return html
    .replace(`<p>${PAYMENT_CONDITIONS_TAG}</p>`, paymentConditionsHtml(conditions))
    .replace(PAYMENT_CONDITIONS_TAG, paymentConditionsLines(conditions).join("<br>"));
}

/** Remplit la balise des modalités de la facture et mémorise le bloc généré. */
export function fillInvoiceOutroPaymentConditions(
  pdfConfig: { outro_offerHTML: string; payment_conditions_html: string },
  conditions: PaymentCondition[],
): void {
  if (!pdfConfig.outro_offerHTML?.includes(PAYMENT_CONDITIONS_TAG)) return;
  pdfConfig.outro_offerHTML = applyPaymentConditionsTag(pdfConfig.outro_offerHTML, conditions);
  pdfConfig.payment_conditions_html = paymentConditionsHtml(conditions);
}

export type PaymentConditionsSync = "UPDATED" | "UNCHANGED" | "EDITED_BY_USER" | "NOT_PRESENT";

/** Régénère le bloc de conditions s'il est intact ; EDITED_BY_USER s'il a été retouché (rien n'est écrasé). */
export function syncInvoiceOutroPaymentConditions(
  pdfConfig: { outro_offerHTML: string; payment_conditions_html: string },
  conditions: PaymentCondition[],
): PaymentConditionsSync {
  const previous = pdfConfig.payment_conditions_html;
  if (!previous) return "NOT_PRESENT";
  const next = paymentConditionsHtml(conditions);
  if (!pdfConfig.outro_offerHTML?.includes(previous)) return "EDITED_BY_USER";
  if (previous === next) return "UNCHANGED";
  pdfConfig.outro_offerHTML = pdfConfig.outro_offerHTML.replace(previous, next);
  pdfConfig.payment_conditions_html = next;
  return "UPDATED";
}

/** Ajoute les conditions en fin de modalités (bloc d'origine retouché, donc introuvable). */
export function appendInvoiceOutroPaymentConditions(
  pdfConfig: { outro_offerHTML: string; payment_conditions_html: string },
  conditions: PaymentCondition[],
): void {
  const next = paymentConditionsHtml(conditions);
  pdfConfig.outro_offerHTML = (pdfConfig.outro_offerHTML ?? "") + next;
  pdfConfig.payment_conditions_html = next;
}
